import { M1_PERMISSIONS, M2_PERMISSIONS, PERMISSIONS } from '@/config/permissions';
import { USER_ROLE_BY_CODE, type RoleCode } from '@/config/role-codes';
import {
  isSidebarNavItemVisible,
  moduleSidebarNavItems,
  settingsSidebarNavItem,
  type SidebarNavItem,
} from '@/config/sidebar-modules';
import type { AccessSnapshot } from '@/types/access';
import type { UserRole } from '@/types/role';
import { hasPermission } from '@/utils/accessControl';

export const DEFAULT_ROUTE = '/dashboard';

type RouteAccessCheckOptions = {
  role: UserRole;
  snapshot: AccessSnapshot;
  /** Skip permission checks while profile is refetching after a role switch. */
  skipPermissionCheck?: boolean;
  /** Feature flags from the user profile; flag-gated routes are blocked unless their flag is true. */
  featureFlags?: Record<string, boolean>;
};

type RouteRule = {
  matches: (pathname: string) => boolean;
  navItem: SidebarNavItem;
  requiredPermission?: string;
};

const getModuleSidebarNavItem = (id: string): SidebarNavItem => {
  const navItem = moduleSidebarNavItems.find((item) => item.id === id);
  if (!navItem) {
    throw new Error(`Sidebar nav item with id "${id}" not found in moduleSidebarNavItems`);
  }
  return navItem;
};

const complianceControlsNavItem = getModuleSidebarNavItem('m1');
const documentsNavItem = getModuleSidebarNavItem('m4');
const evidenceManagementNavItem = getModuleSidebarNavItem('m6');
const statementOfApplicabilitySidebarNavItem = getModuleSidebarNavItem('m1');
const riskManagementNavItem = getModuleSidebarNavItem('m3');
const auditManagementNavItem = getModuleSidebarNavItem('m2');

// `location.pathname` keeps a typed trailing slash even though the router still matches the route,
// so `/audit-management/mrm/` would otherwise skip its exact-match rule and fall through to the
// permission-free module rule.
const normalizePathname = (pathname: string): string =>
  pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

const matchesModulePath = (pathname: string, basePath: string): boolean =>
  pathname === basePath || pathname.startsWith(`${basePath}/`);

const ROUTE_RULES: readonly RouteRule[] = [
  {
    matches: (pathname) => pathname === '/user-settings',
    navItem: settingsSidebarNavItem,
    requiredPermission: PERMISSIONS.READ_ROLE_MATRIX,
  },
  {
    matches: (pathname) => pathname === '/documents' || /^\/documents\/[^/]+$/.test(pathname),
    navItem: documentsNavItem,
    requiredPermission: PERMISSIONS.READ_DOCUMENT_REGISTRY,
  },
  {
    matches: (pathname) => pathname === '/compliance-controls/statement-of-applicability',
    navItem: statementOfApplicabilitySidebarNavItem,
    requiredPermission: M1_PERMISSIONS.READ_STATEMENT_OF_APPLICABILITY,
  },
  {
    matches: (pathname) => matchesModulePath(pathname, '/compliance-controls'),
    navItem: complianceControlsNavItem,
  },
  {
    matches: (pathname) => matchesModulePath(pathname, '/evidence-management'),
    navItem: evidenceManagementNavItem,
  },
  {
    matches: (pathname) => matchesModulePath(pathname, '/risk-management'),
    navItem: riskManagementNavItem,
  },
  {
    matches: (pathname) =>
      pathname === '/audit-management/audit-reports' ||
      pathname === '/audit-management/finding-register',
    navItem: auditManagementNavItem,
    requiredPermission: M2_PERMISSIONS.READ_AUDIT_REGISTER,
  },
  {
    matches: (pathname) => pathname === '/audit-management/mrm',
    navItem: auditManagementNavItem,
    requiredPermission: M2_PERMISSIONS.READ_AUDIT_MRM_MEETING,
  },
  {
    matches: (pathname) => matchesModulePath(pathname, '/audit-management'),
    navItem: auditManagementNavItem,
  },
];

const isKnownRoleCode = (code: string): code is RoleCode => code in USER_ROLE_BY_CODE;

export const resolveRoleFromCode = (
  activeRoleCode: string | undefined,
  fallbackRole: UserRole
): UserRole => {
  if (activeRoleCode && isKnownRoleCode(activeRoleCode)) {
    return USER_ROLE_BY_CODE[activeRoleCode];
  }
  return fallbackRole;
};

const isRouteRuleAccessible = (rule: RouteRule, options: RouteAccessCheckOptions): boolean => {
  if (
    !isSidebarNavItemVisible(rule.navItem, {
      role: options.role,
      snapshot: options.snapshot,
      useRoleFallback: !!options.skipPermissionCheck,
      featureFlags: options.featureFlags,
    })
  ) {
    return false;
  }

  if (
    !options.skipPermissionCheck &&
    rule.requiredPermission &&
    !hasPermission(options.snapshot, rule.requiredPermission)
  ) {
    return false;
  }

  return true;
};

/** True when the route's visibility depends on a profile feature flag, so it can't be decided before the profile loads. */
export const isFeatureFlaggedPath = (pathname: string): boolean =>
  !!ROUTE_RULES.find((entry) => entry.matches(normalizePathname(pathname)))?.navItem.featureFlag;

export const isPathAccessible = (pathname: string, options: RouteAccessCheckOptions): boolean => {
  const rule = ROUTE_RULES.find((entry) => entry.matches(normalizePathname(pathname)));
  if (!rule) return true;
  return isRouteRuleAccessible(rule, options);
};
